import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Data } from '@puckeditor/core';
import { ArrowRight } from 'lucide-react';
import { SiteEditor } from '../features/editor/SiteEditor';
import { useIndexedDbDraft } from '../features/editor/useIndexedDbDraft';
import { fetchSiteContent, publishSite } from '../features/editor/api';
import {
  absolutizeImageUrls,
  relativizeImageUrls,
} from '../features/editor/imageUrlRewrite';
import { SiteConfigDrawer } from '../components/sites/SiteConfigDrawer';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { LoadingState } from '@/components/ui/loading-state';
import { useDelayedLoader } from '@/hooks/useDelayedLoader';
import { SiteStatusBadge } from '../components/sites/SiteStatusBadge';
import { SITE_STATUS } from '../constants/siteStatus';
import { useSite } from '../hooks/useSites';
import { useAuth } from '../auth/auth0-provider';
import { APP_CONFIG } from '../config/app.config';
import { useBreadcrumbLabel } from '../contexts/BreadcrumbContext';
import { useToast } from '@/contexts/ToastContext';
import { Time } from '@/components/ui/time';

/**
 * Live-site editor. Loads the current bundle from main and exposes Publish
 * as the only persistence action. IndexedDB keeps a local copy purely for
 * device-local crash recovery.
 *
 * The editor is gated by site status: only `live` sites are editable. Sites
 * in `draft`, `suspended`, or `offline` show a status banner instead of the
 * editor — the user has to (re-)publish from the site detail page first.
 * Lifecycle transitions are atomic + webhook-driven, so there's no polling.
 */
export const EditSite = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const { isAuthenticated, isSdkReady } = useAuth();
  const { data: siteData, isLoading, error: queryError, refetch: refetchSite } = useSite(siteIdParam || '');
  const site = siteData ?? null;
  const loading = isLoading || !isAuthenticated || !isSdkReady;
  const error = queryError instanceof Error ? queryError.message : null;
  const refetch = () => { void refetchSite(); };
  const siteId = site?.id;
  const isLive = site?.status === SITE_STATUS.LIVE;
  // Base URL for rewriting bundle image paths between portable storage form
  // (`/images/foo.jpg`) and editor display form (absolute against the site's
  // deployed host). Fall back to `<subdomain>.<sites-domain>` when the API
  // hasn't surfaced an explicit URL yet.
  const siteHost = site?.url
    || (site?.subdomain ? `https://${site.subdomain}.${APP_CONFIG.SITES_DOMAIN}` : '');

  const [data, setData] = useState<Data | null>(null);
  const [themeCss, setThemeCss] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [meta, setMeta] = useState<Record<string, unknown>>({});
  const [loadedFromSha, setLoadedFromSha] = useState<string>('');
  const [publishing, setPublishing] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);

  const idb = useIndexedDbDraft(`site:${siteId ?? ''}`);

  // Mirror SiteRoute's display-name resolution: settings.domain → settings.subdomain.<host> → site.subdomain.<host>.
  const displayName = ((): string => {
    if (!site) return '';
    let raw: unknown = site.config;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch { raw = null; }
    }
    const obj = raw as { settings?: Record<string, unknown> } | Record<string, unknown> | null;
    const settings = ((obj as { settings?: Record<string, unknown> })?.settings || obj || {}) as Record<string, unknown>;
    if (typeof settings.domain === 'string' && settings.domain) return settings.domain;
    if (typeof settings.subdomain === 'string' && settings.subdomain) return `${settings.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
    if (site.subdomain) return `${site.subdomain}.${APP_CONFIG.SITES_DOMAIN}`;
    return '';
  })();

  // Register the parent /sites/:siteId crumb label. SiteRoute does the same
  // when mounted, but EditSite is a top-level route so it owns this here.
  useBreadcrumbLabel(displayName || undefined, siteId ? `/sites/${siteId}` : undefined);

  // Fetch the bundle only after the site is confirmed published. For any
  // other status the API may not have a bundle to return yet, and even if
  // it did the user can't edit it from this page.
  useEffect(() => {
    if (!siteId || !isLive) return;
    let cancelled = false;
    (async () => {
      try {
        const content = await fetchSiteContent(siteId);
        if (cancelled) return;
        setThemeCss(content.themeCss);
        setCustomCss(content.customCss);
        setMeta((content.meta ?? {}) as Record<string, unknown>);
        setLoadedFromSha(content.base_ref);
        // Rewrite portable `/images/...` paths to absolute URLs against
        // the deployed host so Puck's `<img>` previews resolve correctly
        // inside the editor (which lives on a different origin).
        if (idb.recovered && idb.recovered.loadedFromSha === content.base_ref) {
          setData(absolutizeImageUrls(idb.recovered.data, siteHost) as Data);
        } else {
          setData(absolutizeImageUrls(content.data, siteHost) as Data);
        }
      } catch (err) {
        if (!cancelled) setContentError(err instanceof Error ? err.message : 'Failed to load site.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId, isLive, idb.recovered, siteHost]);

  // IndexedDB autosave for crash recovery while editing.
  useEffect(() => {
    if (!data || !idb.ready || !siteId) return;
    const timer = setTimeout(() => {
      void idb.save({ data, themeCss, customCss, meta }, loadedFromSha);
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, themeCss, customCss, meta, loadedFromSha, idb.ready, siteId]);

  const onPublish = async () => {
    if (!siteId || !data) return;
    setPublishing(true);
    try {
      // Reverse the load-time absolutize so the persisted bundle keeps
      // hostname-portable `/images/...` paths.
      const bundleData = relativizeImageUrls(data, siteHost) as Data;
      await publishSite(siteId, { data: bundleData, themeCss, customCss, meta });
      await idb.clear();
      refetch();
      toast.success('Changes are rolling out now.', 'Site published');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Publish failed.';
      setContentError(message);
      toast.error(message, 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // True while we're fetching either the site row or the editor content.
  // Both phases share the single `<LoadingState />` so the user never sees
  // a plain "Loading…" text intermediate state.
  const editorLoading =
    (loading && !site) ||
    (!!site && isLive && !contentError && !data);
  const showEditorLoader = useDelayedLoader(editorLoading);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="w-full pt-5 flex flex-col flex-1 min-h-0">
        <Breadcrumbs />
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              {displayName ? `Edit: ${displayName}` : 'Edit site'}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Make changes to text, images, and layout. Nothing goes live until you publish.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isLive && site?.updatedAt && (
              <span className="text-sm text-muted-foreground">
                Last published <Time value={site.updatedAt} format="relative" />
              </span>
            )}
            {isLive ? (
              <>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={() => setConfigDrawerOpen(true)}
                >
                  Edit configuration
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={onPublish}
                  disabled={publishing || !data}
                >
                  {publishing ? 'Publishing…' : 'Publish'}
                  <ArrowRight className="h-4 w-4 stroke-[1.5]" />
                </Button>
              </>
            ) : site ? (
              <span className="text-sm text-muted-foreground">
                Not publishable yet
              </span>
            ) : null}
          </div>
        </div>

        {error && (
          <p className="py-12 text-center text-sm text-destructive">{error}</p>
        )}

        {site && !isLive && (
          <div className="flex flex-col items-center text-center gap-4 py-16">
            <SiteStatusBadge status={site.status} />
            <h2 className="text-xl font-medium text-foreground">
              This site isn't ready to edit yet
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              You can make changes once the deployment finishes and the site is live.
              We'll automatically refresh this page when it's ready.
            </p>
            <Button
              type="button"
              variant="tertiary"
              size="dense"
              onClick={() => navigate(siteId ? `/sites/${siteId}` : '/sites')}
            >
              Back to site
            </Button>
          </div>
        )}

        {site && isLive && contentError && (
          <p className="py-12 text-center text-sm text-destructive">{contentError}</p>
        )}

        {showEditorLoader && <LoadingState />}

        {site && isLive && !contentError && data && (
          <SiteEditor
            mode="existing"
            initialData={data}
            themeCss={themeCss}
            customCss={customCss}
            editorViewport={meta.editorViewport as 'desktop' | 'tablet' | 'mobile' | undefined}
            onChange={setData}
          />
        )}
      </div>

      <SiteConfigDrawer
        open={configDrawerOpen}
        onClose={() => setConfigDrawerOpen(false)}
        title={typeof meta.title === 'string' ? meta.title : ''}
        onTitleChange={(next) => setMeta((prev) => ({ ...prev, title: next }))}
        subdomain={site?.subdomain ?? ''}
        sitesDomain={APP_CONFIG.SITES_DOMAIN}
        favicon={typeof meta.favicon === 'string' ? meta.favicon : ''}
        onFaviconChange={(next) => setMeta((prev) => ({ ...prev, favicon: next }))}
        siteHost={siteHost}
      />
    </div>
  );
};

export default EditSite;
